import { Link } from "react-router";
import { MarketingNav, MarketingFooter } from "../components/layout";
import { NewsletterCapture } from "../components/marketing/NewsletterCapture";

const SITE = "https://schoolconferencego.com";

export function meta() {
  const title = "Conference Night Checklist | SchoolConferenceGo";
  const description =
    "A practical wall checklist for parent-teacher conference night — before, during, and after — free for school admins. Build free, publish at $399/year.";
  return [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: `${SITE}/resources/conference-night-checklist` },
    { property: "og:image", content: `${SITE}/og-image.jpg` },
    { name: "twitter:card", content: "summary_large_image" },
    { rel: "canonical", href: `${SITE}/resources/conference-night-checklist` },
  ];
}

const BEFORE = [
  "Confirm district calendar dates against your building hours — don’t trust last year’s flyer.",
  "Freeze the teacher list (names, rooms, virtual links) before you open booking.",
  "Generate slots once; regenerate only when a day or slot length actually changes.",
  "Preview as a parent on your phone. If you need a login to book, stop and fix that.",
  "Send one booking path through your channels — newsletter, classroom app, front desk. Parallel links create double-books.",
  "Decide Zero-Roster (families self-select) vs Invitation Mode before anyone uploads a list.",
  "Test one confirmation email and one SMS to yourself. Credits are real; tests prove the night will work.",
];

const DURING = [
  "Put the live dashboard on a wall or laptop at the office — fill rates beat guesswork.",
  "When a teacher is out, reschedule in the tool so affected families get notified automatically.",
  "Keep the booking link handy for walk-ups; don’t invent a paper sidebar that drifts from the system.",
  "If the Wi-Fi wobbles, the schedule still lives in the cloud — refresh, don’t rebuild on paper.",
];

const AFTER = [
  "Export or screenshot fill rates while the night is fresh — next year’s planning starts here.",
  "Note which teachers ran long; adjust slot length before spring conferences.",
  "Thank families who booked early; the quiet ones are your reminder-system proof.",
  "If you also run signups or ticketed nights, you’re already the ICP for the rest of Sizzle — no rush, no ads on family pages.",
];

export default function ConferenceNightChecklistPage() {
  return (
    <div className="min-h-[100dvh] bg-[var(--color-paper)] text-[var(--color-ink)]">
      <MarketingNav active="insights" />

      <article>
        <header className="border-b border-[var(--color-hairline)] bg-white">
          <div className="mx-auto max-w-[720px] px-6 pb-10 pt-12 lg:pt-14">
            <p className="scg-label mb-2.5 text-[var(--color-board)] opacity-55">
              Free lead magnet · agent-drafted · human review welcome
            </p>
            <h1 className="mb-4 text-[clamp(1.7rem,3.8vw,2.45rem)] font-extrabold leading-[1.15] tracking-[-0.02em]">
              Conference Night Checklist
            </h1>
            <p className="text-[1.05rem] leading-[1.65] text-[var(--color-ash)]">
              For the one administrator who has personally survived conference-night chaos: 400 families, 24 teachers,
              and a phone that will not stop ringing. Print this, tape it near the office laptop, and run the night
              once — not three competing systems at once.
            </p>
          </div>
        </header>

        <div className="mx-auto max-w-[720px] px-6 py-10 md:py-12">
          <section className="mb-10 rounded-[var(--radius-md)] border border-[var(--color-hairline)] bg-[var(--color-panel)] p-6">
            <h2 className="mb-2 text-[1.2rem] font-bold tracking-[-0.01em]">
              Email me this checklist
            </h2>
            <p className="mb-4 text-[0.95rem] leading-[1.55] text-[var(--muted)]">
              We’ll send a confirmation for The Folding Table (twice a month) and keep this page bookmarked in your
              inbox trail. Adult work emails only — never student data.
            </p>
            <NewsletterCapture
              sourceComponent="conference-checklist"
              variant="inline"
              interests={["newsletter", "suite", "schoolconferencego", "conference-checklist"]}
              offer="conference-night-checklist"
            />
          </section>

          <ChecklistBlock title="Before booking opens" items={BEFORE} />
          <ChecklistBlock title="During conference night" items={DURING} />
          <ChecklistBlock title="The morning after" items={AFTER} />

          <section className="mb-10">
            <h2 className="mb-3 text-[1.35rem] font-bold tracking-[-0.01em]">
              When you’re ready to stop rebuilding the grid
            </h2>
            <p className="mb-4 text-[0.98rem] leading-[1.65] text-[var(--color-ink)]/85">
              SchoolConferenceGo lets you build and preview the entire night free. You pay{" "}
              <strong className="font-semibold">$399 per school per year</strong> when you publish — unlimited
              conferences, teachers, and families. Families book in one tap with no login and no app. Zero-Roster Mode
              means you can run conferences without uploading any student data.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" className="btn btn-primary no-underline min-h-[44px]">
                Start building free
              </Link>
              <Link to="/guides" className="btn btn-secondary no-underline min-h-[44px]">
                State guides
              </Link>
            </div>
          </section>
        </div>
      </article>

      <MarketingFooter />
    </div>
  );
}

function ChecklistBlock({ title, items }: { title: string; items: string[] }) {
  return (
    <section className="mb-10">
      <h2 className="mb-3 text-[1.35rem] font-bold tracking-[-0.01em]">{title}</h2>
      <ul className="space-y-3">
        {items.map((item) => (
          <li
            key={item}
            className="flex gap-3 border-b border-[var(--color-hairline)] pb-3 text-[0.98rem] leading-[1.6] text-[var(--color-ink)]/90 last:border-0"
          >
            <span
              className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-sm border-2 border-[var(--accent)]"
              aria-hidden
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
